"use client";

import type { CountedActivityType } from "@/app/api/activity-types/route";
import {
  type DiscoverSelection,
  serializeSelection,
} from "@/components/discover/filters/selection";
import type { Tab } from "@/components/tabs/SwipeableTabsController";
import { logger } from "@/lib/logger";
import type { Coords } from "@/types/places";
import { useQuery } from "@tanstack/react-query";

interface UseActivityTypesQueryOptions {
  coords: Coords | null;
  type?: Tab;
  selection: DiscoverSelection;
}

/**
 * The counts wire: canonical selection params plus lat/lon (location is
 * resolution, not selection) plus type (index choice). The route decodes the
 * selection half with parseSelection — one codec on both sides of the seam.
 */
function buildWireParams(opts: UseActivityTypesQueryOptions): URLSearchParams {
  const coords = opts.coords;
  if (!coords) {
    throw new Error("Coords are required");
  }

  const params = serializeSelection(opts.selection);
  params.set("lat", coords.lat.toString());
  params.set("lon", coords.lon.toString());
  // "lng" keeps stale server instances working during a deploy window
  params.set("lng", coords.lon.toString());
  if (opts.type) {
    params.set("type", opts.type);
  }

  return params;
}

/**
 * Cache identity: the canonical serialization (one entry per equivalent
 * selection spelling, new registry filters included automatically) plus
 * coordinates rounded to 2 decimals (~1.1km), the same rule as fetchHomeData.
 */
function generateQueryKey(opts: UseActivityTypesQueryOptions): unknown[] {
  return [
    "activity-types",
    opts.coords
      ? `${opts.coords.lat.toFixed(2)},${opts.coords.lon.toFixed(2)}`
      : null,
    opts.type ?? null,
    serializeSelection(opts.selection).toString(),
  ];
}

/**
 * Fetches activity types from the API
 */
async function fetchActivityTypes(
  opts: UseActivityTypesQueryOptions,
  signal?: AbortSignal,
): Promise<CountedActivityType[]> {
  const params = buildWireParams(opts);

  const response = await fetch(
    `${process.env.NEXT_PUBLIC_BASE_PATH || ""}/api/activity-types?${params}`,
    {
      headers: {
        "Content-Type": "application/json",
      },
      signal,
    },
  );

  if (!response.ok) {
    logger.warn({
      msg: "Failed to fetch activity types",
      status: response.status,
    });
    return [];
  }

  const json = await response.json();

  if (!Array.isArray(json)) {
    logger.warn({ msg: "Invalid activity types response", json });
    return [];
  }

  return json;
}

/**
 * React Query hook for fetching per-activity result counts for a selection.
 */
export function useActivityTypesQuery(
  opts: UseActivityTypesQueryOptions,
): CountedActivityType[] {
  const { data = [] } = useQuery({
    queryKey: generateQueryKey(opts),
    queryFn: async ({ signal }) => {
      try {
        return await fetchActivityTypes(opts, signal);
      } catch (error) {
        // Don't log abort errors
        if (error instanceof Error && error.name === "AbortError") {
          throw error;
        }

        logger.error({ msg: "Error fetching activity types", error });
        return [];
      }
    },
    enabled: opts.coords !== null,
    staleTime: 60_000, // 1 minute - matches original revalidate
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  return data;
}
